'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  filterGeneratorModels,
  getGeneratorAvailableRatios,
  getGeneratorDynamicSettings,
  getGeneratorPriceValue,
  getValidGeneratorModelSlug,
  modelRequiresGeneratorPrompt,
  resolveGeneratorRepeatPayload,
  type AIModel,
  type GeneratorGenerationType,
  type GeneratorRepeatPayload,
  type GeneratorTrendPreview,
} from '@repo/shared';

export type GeneratorState = 'collapsed' | 'medium' | 'expanded';
export type PanelType =
  | 'type'
  | 'model'
  | 'ratio'
  | 'resolution'
  | 'duration'
  | 'mode'
  | 'quality'
  | null;

export type ActiveTrend = GeneratorTrendPreview;

export interface GeneratorBarBridge {
  consumeStoredRepeat?: () => GeneratorRepeatPayload | null;
  subscribeRepeat?: (
    callback: (payload: GeneratorRepeatPayload) => void,
  ) => () => void;
  subscribeVisibility?: (callback: (visible: boolean) => void) => () => void;
}

interface UseGeneratorBarStateParams {
  activeChatId: string | null;
  models: AIModel[] | undefined;
  hasImages: boolean;
  onPreloadInputImage: (url: string) => void;
  formatPrice: (value: string) => string;
  bridge?: GeneratorBarBridge;
  // Seconds extracted from a user-uploaded video. Drives per-second price
  // display for motion-control models — null while no video is attached.
  uploadedVideoDuration?: number | null;
  // Asset-presence flags that feed tokenRateMultiplier discounts (e.g.
  // Seedance reference-to-video charges less when a video reference is
  // supplied). Derived in the bar component from upload state.
  hasInputVideo?: boolean;
  hasInputAudio?: boolean;
}

export function useGeneratorBarState({
  activeChatId,
  models,
  hasImages,
  onPreloadInputImage,
  formatPrice,
  bridge,
  uploadedVideoDuration = null,
  hasInputVideo = false,
  hasInputAudio = false,
}: UseGeneratorBarStateParams) {
  const [state, setState] = useState<GeneratorState>('collapsed');
  const [prompt, setPrompt] = useState('');
  const [promptError, setPromptError] = useState(false);
  const [activeTrend, setActiveTrend] = useState<ActiveTrend | null>(null);
  const [generationType, setGenerationType] =
    useState<GeneratorGenerationType>('image');
  const [selectedModelSlug, setSelectedModelSlug] = useState<string | null>(null);
  const [activePanel, _setActivePanel] = useState<PanelType>(null);
  const activePanelRef = useRef<PanelType>(null);
  const [selectedRatio, setSelectedRatio] = useState<string | null>(null);
  const [selectedResolution, setSelectedResolution] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [selectedQuality, setSelectedQuality] = useState<string | null>(null);
  const [generateAudio, setGenerateAudio] = useState(true);
  const [visible, setVisible] = useState(true);
  const chatDefaultAppliedRef = useRef<string | null>(null);

  const setActivePanel = useCallback(
    (value: PanelType | ((prev: PanelType) => PanelType)) => {
      _setActivePanel((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        activePanelRef.current = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    if (!bridge?.subscribeVisibility) return;
    return bridge.subscribeVisibility(setVisible);
  }, [bridge]);

  useEffect(() => {
    if (!activeChatId) {
      chatDefaultAppliedRef.current = null;
      return;
    }

    if (chatDefaultAppliedRef.current !== activeChatId) {
      chatDefaultAppliedRef.current = activeChatId;
      setGenerationType('chat');
    }
  }, [activeChatId]);

  const filteredModels = filterGeneratorModels(models, generationType);

  useEffect(() => {
    if (!filteredModels.length) return;

    const nextSelectedModelSlug = getValidGeneratorModelSlug(
      filteredModels,
      selectedModelSlug,
    );
    if (nextSelectedModelSlug && nextSelectedModelSlug !== selectedModelSlug) {
      setSelectedModelSlug(nextSelectedModelSlug);
    }
  }, [filteredModels, selectedModelSlug]);

  const currentModel =
    filteredModels.find((model) => model.slug === selectedModelSlug) ?? null;

  // Motion-control models cap billable duration at 10s (character_orientation
  // "image") or 30s ("video") — read it off the model so the UI bills what
  // the API will actually bill (server auto-trims past the cap).
  const motionDefaults = (currentModel?.defaultSettings ?? null) as
    | Record<string, unknown>
    | null;
  const maxBillableVideoDuration =
    motionDefaults?.character_orientation === 'image' ? 10 : 30;

  const currentPriceValue = getGeneratorPriceValue(currentModel, {
    selectedResolution,
    selectedDuration,
    selectedMode,
    selectedQuality,
    selectedRatio,
    generateAudio,
    uploadedVideoDuration,
    maxBillableVideoDuration,
    hasInputVideo,
    hasInputAudio,
    hasInputImage: hasImages,
  });
  const currentPriceLabel = formatPrice(currentPriceValue);
  // When per-second pricing is configured but no duration source is available
  // (no upload, no duration chip), the returned price is the model's base
  // `pricePerGeneration` — consumers can prefix it with "от" to signal that.
  const currentPriceIsFromPrice =
    !!currentModel?.pricingRules &&
    typeof currentModel.pricingRules === 'object' &&
    'pricePerSecond' in (currentModel.pricingRules as Record<string, unknown>) &&
    !uploadedVideoDuration &&
    !selectedDuration;

  const applyRepeatData = useCallback(
    (data: GeneratorRepeatPayload) => {
      const next = resolveGeneratorRepeatPayload(data, models);
      setGenerationType(next.generationType);
      if (next.selectedModelSlug) setSelectedModelSlug(next.selectedModelSlug);
      setActiveTrend(next.activeTrend);
      setPrompt(next.prompt);

      if (next.inputImageUrl) {
        onPreloadInputImage(next.inputImageUrl);
      }

      setState(next.nextState);
    },
    [models, onPreloadInputImage],
  );

  useEffect(() => {
    const pending = bridge?.consumeStoredRepeat?.();
    if (pending) {
      applyRepeatData(pending);
    }
  }, [applyRepeatData, bridge]);

  useEffect(() => {
    if (!bridge?.subscribeRepeat) return;
    return bridge.subscribeRepeat(applyRepeatData);
  }, [applyRepeatData, bridge]);

  const availableRatios = getGeneratorAvailableRatios(currentModel, hasImages);

  useEffect(() => {
    if (!availableRatios.length) return;
    // Keep the user's pick as long as it's still valid for this model.
    // Earlier this branch unconditionally reset to 'auto' whenever 'auto'
    // was first in the list, which made models like seedance-v1-lite
    // appear to ignore aspect-ratio clicks (the click would land, then the
    // effect would re-run on the new `selectedRatio` and bounce it back).
    if (selectedRatio && availableRatios.includes(selectedRatio)) return;
    if (availableRatios.includes('auto')) {
      setSelectedRatio('auto');
      return;
    }
    setSelectedRatio(availableRatios[0] ?? null);
  }, [availableRatios, selectedRatio]);

  // Default to "auto" the moment an image gets attached, but only on the
  // no-image → has-image transition. Earlier this fired on every render
  // (availableRatios is a fresh array each render), so any user click on
  // a different ratio was instantly overwritten back to "auto". Tracking
  // the previous hasImages value as a ref makes this a true one-shot.
  const prevHasImagesRef = useRef(hasImages);
  useEffect(() => {
    const prev = prevHasImagesRef.current;
    prevHasImagesRef.current = hasImages;
    if (!prev && hasImages && availableRatios.includes('auto')) {
      setSelectedRatio('auto');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImages]);

  // Context drives conditional UI in resolutionRules — e.g. Seedance 2.0
  // hides 1080p whenever mode=fast or no image is attached.
  const dynamicSettings = getGeneratorDynamicSettings(currentModel, {
    hasImages,
    selectedMode,
  });

  useEffect(() => {
    setSelectedResolution((prev) =>
      dynamicSettings.availableResolutions.length
        ? dynamicSettings.availableResolutions.includes(prev ?? '')
          ? prev
          : (dynamicSettings.availableResolutions[0] ?? null)
        : null,
    );
    setSelectedDuration((prev) =>
      dynamicSettings.availableDurations.length
        ? dynamicSettings.availableDurations.includes(prev ?? '')
          ? prev
          : (dynamicSettings.availableDurations[0] ?? null)
        : null,
    );
    setSelectedMode((prev) =>
      dynamicSettings.availableModes.length
        ? dynamicSettings.availableModes.includes(prev ?? '')
          ? prev
          : (dynamicSettings.availableModes[0] ?? null)
        : null,
    );
    setSelectedQuality((prev) =>
      dynamicSettings.availableQualities.length
        ? dynamicSettings.availableQualities.includes(prev ?? '')
          ? prev
          : (dynamicSettings.availableQualities[0] ?? null)
        : null,
    );
  }, [currentModel?.slug, dynamicSettings.availableDurations, dynamicSettings.availableModes, dynamicSettings.availableQualities, dynamicSettings.availableResolutions]);

  // Audio toggle was previously reset to `true` inside the dropdown-sync
  // effect above. That effect's deps include `dynamicSettings.availableModes`
  // (and Qualities/etc.) which the helper rebuilds as fresh `[]` references
  // on every render for models with no modes/qualities — so the effect
  // ran on every render, and any user click on "Без звука" was instantly
  // overwritten back to `true`. Tying the reset to slug-only changes
  // means it fires once when the user picks a different model and never
  // again. Default still respects model.defaultSettings.generate_audio
  // (falls back to `true` if missing — matches the existing behavior).
  useEffect(() => {
    const defaults = (currentModel?.defaultSettings ?? null) as
      | Record<string, unknown>
      | null;
    const next = defaults && typeof defaults.generate_audio === 'boolean'
      ? (defaults.generate_audio as boolean)
      : true;
    setGenerateAudio(next);
  }, [currentModel?.slug, currentModel?.defaultSettings]);

  const togglePanel = useCallback(
    (panel: PanelType) => {
      setActivePanel((prev) => (prev === panel ? null : panel));
    },
    [setActivePanel],
  );

  const handleSelectType = useCallback(
    (type: GeneratorGenerationType) => {
      setGenerationType(type);
      setActivePanel(null);
    },
    [setActivePanel],
  );

  const handleSelectModel = useCallback(
    (slug: string) => {
      setSelectedModelSlug(slug);
      setActivePanel(null);
    },
    [setActivePanel],
  );

  const handleSelectRatio = useCallback((ratio: string) => {
    setSelectedRatio(ratio);
  }, []);

  return {
    state,
    setState,
    prompt,
    setPrompt,
    promptError,
    setPromptError,
    activeTrend,
    setActiveTrend,
    generationType,
    setGenerationType,
    selectedModelSlug,
    setSelectedModelSlug,
    activePanel,
    activePanelRef,
    setActivePanel,
    togglePanel,
    selectedRatio,
    setSelectedRatio,
    selectedResolution,
    setSelectedResolution,
    selectedDuration,
    setSelectedDuration,
    selectedMode,
    setSelectedMode,
    selectedQuality,
    setSelectedQuality,
    generateAudio,
    setGenerateAudio,
    visible,
    filteredModels,
    currentModel,
    currentPriceValue,
    currentPriceLabel,
    currentPriceIsFromPrice,
    maxBillableVideoDuration,
    availableRatios,
    dynamicSettings,
    handleSelectType,
    handleSelectModel,
    handleSelectRatio,
    modelRequiresPrompt: modelRequiresGeneratorPrompt(currentModel),
  };
}
